import React from 'react';
import { Database, Table, GitBranch, Layers, AlertTriangle, CheckCircle } from 'lucide-react';

interface AutoModelSummaryProps {
  autoModel: any;
}

export const AutoModelSummary: React.FC<AutoModelSummaryProps> = ({ autoModel }) => {
  if (!autoModel) return null;

  const summary = autoModel.model_summary;

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mb-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Database className="w-6 h-6 text-sky-400" />
        <div>
          <h3 className="text-lg font-semibold text-slate-100">Modelo Generado por IA</h3>
          <p className="text-sm text-slate-400">Dominio: {summary.business_domain}</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-900/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Table className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-slate-400 uppercase">Tablas Facts</span>
          </div>
          <div className="text-2xl font-bold text-blue-400">{summary.facts.length}</div>
          <div className="text-xs text-slate-500 mt-1">
            {summary.facts.join(', ') || 'Ninguna'}
          </div>
        </div>

        <div className="bg-slate-900/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Layers className="w-4 h-4 text-purple-400" />
            <span className="text-xs text-slate-400 uppercase">Dimensiones</span>
          </div>
          <div className="text-2xl font-bold text-purple-400">{summary.dimensions.length}</div>
          <div className="text-xs text-slate-500 mt-1">
            {summary.dimensions.slice(0, 3).join(', ')}{summary.dimensions.length > 3 ? '...' : ''}
          </div>
        </div>

        <div className="bg-slate-900/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <GitBranch className="w-4 h-4 text-sky-400" />
            <span className="text-xs text-slate-400 uppercase">Relaciones</span>
          </div>
          <div className="text-2xl font-bold text-sky-400">{autoModel.relationships.length}</div>
          <div className="text-xs text-slate-500 mt-1">Detectadas automáticamente</div>
        </div>

        <div className="bg-slate-900/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-slate-400 uppercase">Jerarquías</span>
          </div>
          <div className="text-2xl font-bold text-amber-400">{autoModel.hierarchies.length}</div>
          <div className="text-xs text-slate-500 mt-1">
            {autoModel.hierarchies.map((h: any) => h.domain).join(', ') || 'Ninguna'}
          </div>
        </div>
      </div>

      {/* Entities Summary */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-slate-300 mb-3">Entidades Detectadas:</h4>
        <div className="flex flex-wrap gap-2">
          {summary.detected_entities.map((entity: string, idx: number) => (
            <span 
              key={idx}
              className="px-3 py-1 bg-slate-700/50 border border-slate-600 rounded-full text-xs text-slate-300"
            >
              {entity}
            </span>
          ))}
        </div>
      </div>

      {/* Lookups & Bridges */}
      {(summary.lookups.length > 0 || summary.bridges.length > 0) && (
        <div className="mb-6">
          {summary.lookups.length > 0 && (
            <div className="mb-3">
              <span className="text-sm text-slate-400">Tablas Lookup: </span>
              <span className="text-sm text-slate-300">{summary.lookups.join(', ')}</span>
            </div>
          )}
          {summary.bridges.length > 0 && (
            <div>
              <span className="text-sm text-slate-400">Tablas Bridge (N:M): </span>
              <span className="text-sm text-slate-300">{summary.bridges.join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {/* Warnings */}
      {summary.warnings.length > 0 && (
        <div className="border-t border-slate-700 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <h4 className="text-sm font-semibold text-amber-400">
              Advertencias ({summary.warnings.length})
            </h4>
          </div>
          <div className="space-y-2">
            {summary.warnings.map((warning: string, idx: number) => (
              <div key={idx} className="flex gap-2 text-xs text-slate-400 bg-amber-900/10 border border-amber-900/30 rounded p-3">
                <span className="text-amber-400 mt-0.5">⚠️</span>
                <span>{warning}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Potential Issues */}
      {summary.potential_issues && summary.potential_issues.length > 0 && (
        <div className="border-t border-slate-700 pt-4 mt-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <h4 className="text-sm font-semibold text-red-400">
              Problemas Potenciales ({summary.potential_issues.length})
            </h4>
          </div>
          <div className="space-y-2">
            {summary.potential_issues.map((issue: string, idx: number) => (
              <div key={idx} className="flex gap-2 text-xs text-slate-400 bg-red-900/10 border border-red-900/30 rounded p-3">
                <span className="text-red-400 mt-0.5">❌</span>
                <span>{issue}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Success Message */}
      {summary.warnings.length === 0 && (!summary.potential_issues || summary.potential_issues.length === 0) && (
        <div className="border-t border-slate-700 pt-4 mt-4">
          <div className="flex items-center gap-2 text-sky-400">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">Modelo generado sin advertencias ni problemas detectados</span>
          </div>
        </div>
      )}
    </div>
  );
};
